import { useMemo, useState, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { ETICHETE_TIP, esteNecesarTeren, type TipCampanie } from '@b4l/shared'
import { api } from '../../lib/api'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { CarduriSelector, type CardSelector } from '../../components/ui/CarduriSelector'
import { StareLista } from '../../components/ui/StareLista'
import { CapSortabil, useSortare } from '../../components/ui/Sortare'
import { LinkAwb } from '../../components/ui/LinkAwb'
import { useAuthStore } from '../../stores/auth'
import { PatratTip } from '../../components/marketing/eveniment/activitate'
import { PanouAprobareCerere } from '../../components/marketing/eveniment/PanouAprobareCerere'
import { CerereNoua } from '../../components/marketing/eveniment/CerereNoua'
import { Button } from '../../components/ui/Button'
import { useQueryClient } from '@tanstack/react-query'
import {
  CULOARE_STARE_CERERE,
  ETICHETA_STARE,
  type Cerere,
} from '../../components/marketing/eveniment/tipuriNecesar'

/**
 * Cererile de marfă din toate activitățile — coada de lucru (PR & Evenimente, etapa 3,
 * 15.10.2026; refăcută ca tabel pe 26.10.2026).
 *
 * Ecran propriu, intrat cu dreptul de sampling: vânzările și operaționalul n-au lista de activități.
 * Clic pe o cerere = panoul cu activitatea alături și decizia (doar adminii aprobă).
 *
 * 26.10.2026: coloanele Agent, Valoare și AWB (Victor), fiecare își vede doar cererile lui (adminii
 * văd tot — filtrul e pe server) și ordinea e cea a trimiterii: cererile se iau la rând, nu după
 * data activității (Madalin). Orice coloană se poate sorta.
 */

type Cheie = 'aprobat' | 'trimis' | 'drum' | 'ciorne' | 'inchise'

const plecata = (c: Cerere) => Boolean(c.awbNumber || c.bonEaDocNo)
const IN: Record<Cheie, (c: Cerere) => boolean> = {
  aprobat: (c) => c.status === 'trimisa',
  // Aprobată, nimic plecat încă. Livrarea directă (AWB/bon) rămâne „aprobată”, dar a plecat.
  trimis: (c) => c.status === 'aprobata' && c.restTotal > 0 && !plecata(c),
  drum: (c) => c.status === 'aprobata' && plecata(c),
  // Ciornele și cele trimise înapoi: amândouă așteaptă autorul.
  ciorne: (c) => c.status === 'ciorna' || c.status === 'returnata',
  inchise: (c) =>
    ['transferata', 'inchisa', 'respinsa'].includes(c.status) ||
    (c.status === 'aprobata' && c.restTotal === 0 && !plecata(c)),
}

function zileDe(iso: string | null | undefined): number | null {
  if (!iso) return null
  const z = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((z(new Date(iso)) - z(new Date())) / 86_400_000)
}

function cand(iso: string | null): string {
  const z = zileDe(iso)
  if (z == null) return '—'
  if (z === 0) return 'azi'
  if (z === -1) return 'ieri'
  return `acum ${-z} zile`
}

export function CereriMarfaPage() {
  const [parametri, setParametri] = useSearchParams()
  const [card, setCard] = useState<Cheie>('aprobat')
  const q = useQuery<{ cereri: Cerere[] }>({
    queryKey: ['necesar', 'toate'],
    queryFn: async () => (await api.get('/marketing/necesar')).data,
  })
  const cereri = useMemo(() => q.data?.cereri ?? [], [q.data])

  const sortare = useSortare<Coloana>('trimisa', { cerere: 'asc', agent: 'asc', awb: 'asc' }, 'asc')
  const { ordoneaza } = sortare
  const lista = useMemo(() => {
    const l = ordoneaza(cereri.filter(IN[card]), valoarePeColoana)
    // Coada (de aprobat, de trimis, pe drum) merge în ordinea trimiterii; închisele și ciornele
    // se citesc de la cea mai nouă, cât timp nimeni n-a ales altă sortare.
    const implicita = sortare.cheie === 'trimisa' && sortare.directie === 'asc'
    return implicita && (card === 'inchise' || card === 'ciorne') ? l.reverse() : l
  }, [cereri, card, ordoneaza, sortare.cheie, sortare.directie])
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const permisiuni = useAuthStore((s) => s.permissions)
  // Aceleași drepturi ca la crearea din fila Necesar: scriere pe sampling sau pe evenimente.
  const poateCere =
    permisiuni.includes('marketing.sampling:write') || permisiuni.includes('marketing.events:write')
  const [noua, setNoua] = useState(false)
  const qc = useQueryClient()

  const alesId = Number(parametri.get('cerere')) || null
  const aleasa = cereri.find((c) => c.id === alesId) ?? null
  const deschide = (id: number | null) => {
    const p = new URLSearchParams(parametri)
    if (id) p.set('cerere', String(id))
    else p.delete('cerere')
    setParametri(p, { replace: true })
  }
  // După o decizie: următoarea cerere de aprobat din coadă, altfel se închide panoul.
  const urmatoarea = () => {
    const deAprobat = cereri
      .filter(IN.aprobat)
      .filter((c) => c.id !== alesId)
      .sort((a, b) => (a.trimisaLa ?? a.createdAt).localeCompare(b.trimisaLa ?? b.createdAt))
    deschide(deAprobat[0]?.id ?? null)
  }

  const nr = (k: Cheie) => cereri.filter(IN[k]).length
  const carduri: CardSelector<Cheie>[] = [
    {
      cheie: 'aprobat',
      eticheta: 'De aprobat',
      valoare: nr('aprobat'),
      detaliu: 'așteaptă un admin',
      ton: nr('aprobat') ? 'rosu' : 'gri',
    },
    {
      cheie: 'trimis',
      eticheta: 'De trimis',
      valoare: nr('trimis'),
      detaliu: 'aprobate, marfa n-a plecat',
      ton: nr('trimis') ? 'galben' : 'gri',
    },
    {
      cheie: 'drum',
      eticheta: 'Pe drum',
      valoare: nr('drum'),
      detaliu: 'plecate cu AWB sau bon',
      ton: 'albastru',
    },
    {
      cheie: 'inchise',
      eticheta: 'Închise',
      valoare: nr('inchise'),
      detaliu: 'trimise, închise, respinse',
      ton: 'gri',
    },
  ]
  const returnate = cereri.filter((c) => c.status === 'returnata').length
  if (nr('ciorne'))
    carduri.splice(3, 0, {
      cheie: 'ciorne',
      eticheta: 'Ciorne și de corectat',
      valoare: nr('ciorne'),
      detaliu: returnate
        ? `${returnate} ${returnate === 1 ? 'trimisă' : 'trimise'} înapoi`
        : 'netrimise încă',
      ton: returnate ? 'galben' : 'gri',
    })

  const tasta = (e: KeyboardEvent, id: number) => {
    // Enter pe linkul AWB din rând îl deschide pe el, nu panoul.
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      deschide(id)
    }
  }

  return (
    <div className="grid gap-4">
      <AntetPagina
        titlu="Cereri de marfă"
        descriere="Orice marfă se cere de aici: pentru tine, pentru o activitate, un colaborator sau un partener."
        actiuni={
          poateCere ? (
            <Button onClick={() => setNoua(true)}>
              <Plus size={16} aria-hidden="true" />
              Cerere nouă
            </Button>
          ) : undefined
        }
      />
      {noua && (
        <CerereNoua
          onClose={() => setNoua(false)}
          onGata={() => {
            void qc.invalidateQueries({ queryKey: ['necesar'] })
          }}
        />
      )}
      <CarduriSelector carduri={carduri} ales={card} onAlege={setCard} eticheta="Stări" />

      <section className="rounded-xl border border-b4l-border bg-b4l-surface">
        <StareLista
          interogare={q}
          gol={!q.isLoading && lista.length === 0}
          mesajGol={
            card === 'aprobat'
              ? 'Nicio cerere de aprobat. Totul e la zi.'
              : card === 'trimis'
                ? 'Nimic de trimis. Aici apar cererile aprobate din care n-a plecat marfa.'
                : 'Nicio cerere aici.'
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border bg-b4l-bg-soft text-[12px] text-b4l-text-muted">
                  <CapSortabil sortare={sortare} coloana="cerere">
                    Cerere
                  </CapSortabil>
                  <CapSortabil sortare={sortare} coloana="agent">
                    Agent
                  </CapSortabil>
                  <CapSortabil sortare={sortare} coloana="trimisa">
                    Trimisă
                  </CapSortabil>
                  <CapSortabil
                    sortare={sortare}
                    coloana="valoare"
                    dreapta
                    explicatie="Preț de cost: intrarea din EA, altfel costul de producție. Cantitatea aprobată, iar până la aprobare cea cerută."
                  >
                    Valoare
                  </CapSortabil>
                  <CapSortabil sortare={sortare} coloana="awb">
                    AWB
                  </CapSortabil>
                  <CapSortabil sortare={sortare} coloana="stare">
                    Stare
                  </CapSortabil>
                </tr>
              </thead>
              <tbody className="divide-y divide-b4l-border">
                {lista.map((c) => {
                  const tip = (c.campanie.tip ?? 'altele') as TipCampanie
                  return (
                    <tr
                      key={c.id}
                      tabIndex={0}
                      onClick={() => deschide(c.id)}
                      onKeyDown={(e) => tasta(e, c.id)}
                      className={`cursor-pointer align-middle outline-none hover:bg-b4l-bg-soft focus-visible:bg-b4l-bg-soft ${
                        c.id === alesId ? 'bg-b4l-blue-light' : ''
                      }`}
                    >
                      <td className="px-3 py-2.5">
                        <span className="flex min-w-[220px] items-center gap-3">
                          <PatratTip tip={tip} eticheta={ETICHETE_TIP[tip] ?? ''} />
                          <span className="grid min-w-0 gap-px">
                            <b className="truncate font-medium text-b4l-text">
                              #{c.id} · {c.campanie.titlu}
                            </b>
                            <span className="truncate text-mic text-b4l-text-dim">
                              {pentruCine(c)} · {c.linii.length}{' '}
                              {c.linii.length === 1 ? 'produs' : 'produse'}
                              {c.motiv ? ` · „${c.motiv}”` : ''}
                            </span>
                            {c.status === 'returnata' && c.motivRespingere && (
                              <span className="truncate text-mic text-b4l-amber-dark">
                                De corectat: {c.motivRespingere}
                              </span>
                            )}
                          </span>
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-b4l-text">
                        {numeAgent(c)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-b4l-text-muted">
                        {cand(c.trimisaLa ?? c.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                        <Valoare c={c} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <CelulaAwb c={c} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <span
                          className={`rounded-full px-2 py-px text-[11.5px] font-semibold ${CULOARE_STARE_CERERE[c.status] ?? ''}`}
                        >
                          {plecata(c) && c.status === 'aprobata'
                            ? 'Plecată'
                            : (ETICHETA_STARE[c.status] ?? c.status)}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </StareLista>
      </section>
      <p className="text-mic text-b4l-text-dim">
        Cererile se iau la rând, în ordinea în care au fost trimise.{' '}
        {eAdmin
          ? 'Ca admin le vezi pe toate; ceilalți își văd doar cererile lor.'
          : 'Vezi doar cererile tale.'}{' '}
        Notificarea și emailul unei cereri duc direct la ea.
      </p>

      {aleasa && (
        <PanouAprobareCerere
          cerere={aleasa}
          onInchide={() => deschide(null)}
          onDecis={urmatoarea}
        />
      )}
    </div>
  )
}

type Coloana = 'cerere' | 'agent' | 'trimisa' | 'valoare' | 'awb' | 'stare'

function numeAgent(c: Cerere): string {
  return c.ceruteDe?.name ?? c.ceruteDe?.email ?? '—'
}

function valoarePeColoana(c: Cerere, k: Coloana): string | number | null {
  switch (k) {
    case 'cerere':
      return c.id
    case 'agent':
      return numeAgent(c)
    case 'trimisa':
      return c.trimisaLa ?? c.createdAt
    case 'valoare':
      return c.valoare ?? null
    case 'awb':
      return c.awbNumber ?? null
    case 'stare':
      return ETICHETA_STARE[c.status] ?? c.status
  }
}

/** Cui îi pleacă marfa, pe scurt. */
function pentruCine(c: Cerere): string {
  if (c.tip === 'colaborator') return `pentru ${c.influencer?.handle ?? 'un colaborator'}`
  if (c.tip === 'partener') return `pentru ${c.client?.name ?? 'un partener'}`
  if (esteNecesarTeren(c.campanie.cod)) return 'pentru teren'
  return c.actiune ? `pentru ${c.actiune.denumire}` : 'pentru activitate'
}

const lei = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 })

function Valoare({ c }: { c: Cerere }) {
  if (c.valoare == null)
    return (
      <span className="text-b4l-text-dim" title="Niciun produs din cerere n-are preț de cost.">
        —
      </span>
    )
  return (
    <span
      className="font-semibold text-b4l-text"
      title={c.valoareIncompleta ? 'Unele produse n-au preț: valoarea e mai mare.' : undefined}
    >
      {c.valoareIncompleta ? 'peste ' : ''}
      {lei.format(c.valoare)} lei
    </span>
  )
}

/**
 * AWB-ul, când marfa pleacă prin curier. Altfel nu există: ridicarea din depozit și transferul
 * în gestiunea agentului n-au AWB (Madalin, 26.10.2026 — „nu trebuie să apară un AWB obligatoriu").
 */
function CelulaAwb({ c }: { c: Cerere }) {
  if (c.awbNumber)
    return (
      <span className="grid gap-px">
        <LinkAwb curier="FAN" awb={c.awbNumber} />
        {c.stareAwb?.livratLa ? (
          <span className="text-[11px] text-b4l-green-dark">livrat</span>
        ) : c.stareAwb?.ultimaMiscare ? (
          <span className="max-w-[160px] truncate text-[11px] text-b4l-text-dim">
            {c.stareAwb.ultimaMiscare}
          </span>
        ) : null}
      </span>
    )
  // Colaboratorul și partenerul primesc marfa prin curier și fără bifă (`expediazaDirect`).
  const curier = c.prinCurier || (c.tip != null && c.tip !== 'activitate')
  if (curier)
    return ['aprobata', 'transferata', 'trimisa'].includes(c.status) ? (
      <span className="text-mic text-b4l-amber-dark">curier · de emis</span>
    ) : (
      <span className="text-b4l-text-dim">—</span>
    )
  if (c.linii.some((l) => l.transferat > 0))
    return <span className="text-mic text-b4l-text-dim">transfer, fără AWB</span>
  return <span className="text-b4l-text-dim">—</span>
}
